import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, CreditCard, AlertTriangle, Loader2 } from 'lucide-react';
import { fetchCashWallet, fetchPaymentMethods, topupWallet, type TopupResult } from '@/lib/api';
import { cashWalletQueryKey } from '@/lib/cashWalletKey';
import { useAuthStore } from '@/store/auth';
import { currencySymbol, formatFee, formatMoneyExplicit } from '@/lib/money';
import { defaultCardSummary } from '@/lib/defaultCard';
import { walletPageState } from './walletPageState';
import { WalletBalanceList } from './WalletBalanceList';
import { WalletCreditList } from './WalletCreditList';
import { TopupConfirmDialog } from './TopupConfirmDialog';
import {
  QUICK_AMOUNTS, createTopupActions, topupAmountValid, topupFlowReducer,
  type TopupFlowAction, type TopupFlowState, type TopupOrder, type TopupRules,
} from './topupFlow';
import styles from './WalletPage.module.css';

interface Props {
  onNavigate: (path: string) => void;
  /** The page's query string; the browser's by default. */
  search?: string;
}

/**
 * Driver-facing wallet: every wallet the driver has (one per currency),
 * a top-up form, status copy around the pre-loaded minimum + missing
 * payment method.
 *
 * Currency (Warren, 2026-10-25): "each wallet gets a currency, a CAD wallet
 * pays CAD sessions, and CAD top-ups are added." Each wallet shows with its
 * currency, all of them whatever the "Top up in" toggle says; the toggle
 * only picks the wallet the top-up goes to. A driver who charges in Canada
 * can top up a CAD wallet (the Api lists the currencies in topUpCurrencies;
 * the screen before a CAD start links here with
 * ?currency=CAD&amount=<shortfall>). A USD wallet also pays at a Canadian
 * charger, converted at the day's Bank of Canada rate
 * (docs/billing/wallet-currency.md); money never moves between wallets.
 *
 * Top-up (Warren, 2026-10-25: "There should be a confirmation before topping
 * up"): a preset chip or the amount field only sets the amount. "Top up"
 * opens a confirmation naming the amount and currency, the card that will
 * be charged and the wallet that receives it. Only "Confirm" charges: an
 * off-session Stripe charge on that card, in the chosen currency, sent once
 * (topupFlow.ts). The page then shows the result + any bonus credit (from
 * an auto-applied wallet_topup coupon, USD only).
 *
 * Make-good credits (Warren, 2026-10-28): each active one shows under the
 * balances with what is left of it, its expiry and its reason
 * (WalletCreditList). It is already inside the balance of its currency.
 *
 * "Add a payment method" is intentionally a deep-link to Profile →
 * Payment Methods today; we don't reimplement card collection here.
 */
export function WalletPage({ onNavigate, search }: Props) {
  const { t, i18n } = useTranslation('wallet');
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?._id);
  const walletQ = useQuery({
    queryKey: cashWalletQueryKey(userId),
    queryFn: fetchCashWallet,
    staleTime: 15_000,
  });
  // The card the confirmation names (and the top-up charges).
  const paymentQ = useQuery({
    queryKey: ['payment-methods'],
    queryFn: fetchPaymentMethods,
  });
  // The link from the screen before a start: the currency to top up and
  // the amount that would make the wallet usable there.
  const [query] = useState(() => new URLSearchParams(search ?? (typeof window !== 'undefined' ? window.location.search : '')));
  const [picked, setPicked] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<TopupResult | null>(null);

  // The top-up flow. Its state also lives in a ref, so the actions always
  // read the latest step (a second Confirm tap before a re-render included).
  const [flow, setFlow] = useState<TopupFlowState>(() => {
    const n = Number(query.get('amount'));
    return { amountCents: Number.isSafeInteger(n) && n > 0 ? n : '', phase: 'edit' };
  });
  const flowRef = useRef(flow);
  const wallet = walletQ.data;
  const view = wallet ? walletPageState(wallet, query.get('currency'), picked) : null;
  const card = defaultCardSummary(paymentQ.data);
  const rules: TopupRules = {
    minTopupCents: wallet?.minTopupCents ?? Number.POSITIVE_INFINITY,
    hasPaymentMethod: Boolean(wallet?.hasDefaultPaymentMethod),
  };
  const order: TopupOrder = {
    currency: view?.multiCurrency ? view.topUpCurrency : undefined,
    paymentMethodId: card.id ?? undefined,
  };
  const live = useRef({ rules, order });
  live.current = { rules, order };

  const actions = useMemo(() => createTopupActions({
    getState: () => flowRef.current,
    dispatch: (action: TopupFlowAction) => {
      flowRef.current = topupFlowReducer(flowRef.current, action);
      setFlow(flowRef.current);
    },
    rules: () => live.current.rules,
    order: () => live.current.order,
    charge: topupWallet,
    onReset: () => { setSuccess(null); setError(null); },
    onSuccess: (res) => {
      setSuccess(res);
      qc.invalidateQueries({ queryKey: ['wallet'] });
      qc.invalidateQueries({ queryKey: ['gamification'] });
      // The screen before a start re-runs its gate with the new balance.
      qc.invalidateQueries({ queryKey: ['start-preview'] });
    },
    onError: (err) => { void topupErrorMessage(err, t('topup.failed')).then(setError); },
  }), [qc, t]);

  if (walletQ.isLoading && !wallet) {
    return <div className={styles.page}><p className={styles.loading}>{t('loading')}</p></div>;
  }
  if (!wallet || !view) {
    return <div className={styles.page}><p className={styles.loading}>{t('unavailable')}</p></div>;
  }

  const currency = view.topUpCurrency;
  const money = (cents: number, c: string = currency) => formatMoneyExplicit(cents, c, i18n.language);
  const noPaymentMethod = !wallet.hasDefaultPaymentMethod;
  const selectedWallet = view.wallets.find((w) => w.currency === currency) ?? { currency, balanceCents: 0 };
  const balanceShort = selectedWallet.balanceCents < wallet.minimumBalanceCents;
  const charging = flow.phase === 'charging';
  const amountReady = topupAmountValid(flow.amountCents, rules);

  return (
    <div className={styles.page}>
      <button className={styles.backFab} onClick={() => onNavigate('/profile')} aria-label={t('back')}>
        <ArrowLeft size={18} />
      </button>

      <header className={styles.header}>
        <h1 className={styles.title}>{t('title')}</h1>
        <p className={styles.subtitle}>
          {t('subtitle')}
        </p>
      </header>

      <WalletBalanceList view={view} minimumBalanceCents={wallet.minimumBalanceCents} />
      <WalletCreditList credits={wallet.credits} />

      {/* Status alerts */}
      {noPaymentMethod && (
        <div className={`${styles.alert} ${styles.alertWarn}`}>
          <CreditCard size={16} />
          <div>
            <p className={styles.alertTitle}>{t('noPayment.title')}</p>
            <p className={styles.alertBody}>
              {t('noPayment.body')}
            </p>
            <button className={styles.alertCta} onClick={() => onNavigate('/billing/payment-methods')}>
              {t('noPayment.cta')}
            </button>
          </div>
        </div>
      )}
      {!noPaymentMethod && balanceShort && (
        <div className={`${styles.alert} ${styles.alertWarn}`}>
          <AlertTriangle size={16} />
          <div>
            <p className={styles.alertTitle}>{t('belowMin.title')}</p>
            <p className={styles.alertBody}>
              {t('belowMin.body', { amount: money(wallet.minimumBalanceCents - selectedWallet.balanceCents) })}
            </p>
          </div>
        </div>
      )}

      {/* Top-up panel */}
      <section className={styles.topupCard}>
        <h2 className={styles.cardTitle}>{t('topup.title')}</h2>
        <p className={styles.cardLead}>
          {t('topup.lead')}
        </p>

        {view.multiCurrency && (
          <div className={styles.currencyRow}>
            <span className={styles.customLabel}>{t('topup.currencyLabel')}</span>
            <div className={styles.currencyToggle} role="radiogroup" aria-label={t('topup.currencyLabel')}>
              {view.topUpCurrencies.map((c) => (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={c === currency}
                  className={[styles.currencyBtn, c === currency ? styles.currencyBtnActive : ''].filter(Boolean).join(' ')}
                  disabled={charging}
                  onClick={() => { setPicked(c); setSuccess(null); setError(null); }}
                >
                  {c}
                </button>
              ))}
            </div>
            <p className={styles.smallprint}>
              {t('topup.chooseWallet')}
              {currency !== 'USD' && <> {t('topup.couponUsdOnly')}</>}
            </p>
          </div>
        )}

        {/* A chip only fills the amount; nothing charges until Confirm. */}
        <div className={styles.quickRow}>
          {QUICK_AMOUNTS.map((c) => (
            <button
              key={c}
              type="button"
              className={[styles.quickBtn, flow.amountCents === c ? styles.quickBtnActive : ''].filter(Boolean).join(' ')}
              aria-pressed={flow.amountCents === c}
              disabled={charging || noPaymentMethod || c < wallet.minTopupCents}
              onClick={() => actions.pick(c)}
            >
              <Plus size={12} aria-hidden /> <span className={styles.quickAmount}>{formatFee(c, currency, i18n.language)}</span>
            </button>
          ))}
        </div>

        <div className={styles.customRow}>
          <label className={styles.customLabel} htmlFor="evi-topup-amount">{t('topup.customLabel')}</label>
          <div className={styles.customInputWrap}>
            <span className={styles.dollarSign}>{currencySymbol(currency, i18n.language)}</span>
            <input
              id="evi-topup-amount"
              type="number"
              inputMode="decimal"
              min={wallet.minTopupCents / 100}
              step="0.01"
              className={styles.customInput}
              style={{ paddingLeft: `${Math.max(28, 16 + currencySymbol(currency, i18n.language).length * 11)}px` }}
              value={flow.amountCents === '' ? '' : (flow.amountCents / 100).toFixed(2)}
              disabled={charging}
              onChange={(e) => {
                const n = Number(e.target.value);
                actions.type(e.target.value !== '' && Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : '');
              }}
              placeholder={(wallet.minTopupCents / 100).toFixed(2)}
            />
          </div>
          <button
            type="button"
            className={styles.confirmBtn}
            disabled={charging || !amountReady}
            onClick={() => actions.review()}
          >
            {charging
              ? <><Loader2 size={14} className={styles.spinner} /> {t('topup.charging')}</>
              : amountReady
                ? t('topup.cta', { amount: money(flow.amountCents as number) })
                : t('topup.confirm')}
          </button>
        </div>

        {error && <div className={styles.alertErr} role="alert">{error}</div>}
        {success && (
          <div className={styles.successBlock} role="status">
            <p className={styles.successTitle}>
              {t('success.title', { amount: money(success.requestedCents, success.currency) })}
            </p>
            <p className={styles.successBody}>
              {t('success.charged', { amount: money(success.chargedCents, success.currency) })}
              {success.bonusCreditCents > 0 && (
                <> {t('success.bonus', { amount: money(success.bonusCreditCents, success.currency) })} </>
              )}
              {t('success.newBalance')} <strong>{money(success.newBalanceCents, success.currency)}</strong>.
            </p>
          </div>
        )}

        {/*
         * Editorial: "Funds are held" stays passive — actor (the
         * EVI system) is irrelevant to the user, and the construction
         * is a state descriptor, not an action. The follow-on clause
         * gets the period split + active rewrite ("falls to your
         * default card") because the previous "is charged to" was
         * a weaker passive without the same actor-irrelevant defense.
         */}
        <p className={styles.smallprint}>
          {t('smallprint', { amount: money(wallet.minTopupCents) })}
        </p>
      </section>

      {(flow.phase === 'confirm' || flow.phase === 'charging') && typeof flow.amountCents === 'number' && (
        <TopupConfirmDialog
          amount={money(flow.amountCents)}
          currency={currency}
          card={card.last4 ? card : null}
          walletLabel={t('wallets.label', { currency })}
          charging={charging}
          onConfirm={() => void actions.confirm()}
          onCancel={actions.cancel}
        />
      )}
    </div>
  );
}

/** The Api's message for a refused top-up, else the generic one. */
async function topupErrorMessage(err: unknown, fallback: string): Promise<string> {
  const httpErr = err as { response?: Response; message?: string } | null;
  let msg = httpErr?.message ?? fallback;
  if (httpErr?.response) {
    try {
      const j = await httpErr.response.clone().json() as
        { error?: { message?: string } | string };
      msg = typeof j.error === 'string' ? j.error : (j.error?.message ?? msg);
    } catch { /* keep generic */ }
  }
  return msg;
}
